/**
 * StormField — periodic late-game hazard. A volley of fast-moving
 * shards sweeps across the world along one axis, leaving uneven gaps
 * the player has to thread through. Activates once the cluster's
 * biomass crosses a threshold so early-game stays calm.
 *
 * Lifecycle of one storm:
 *   warn   (~3s)  — banner up, no shards yet, gives the player time
 *                   to read the storm's direction (an arrow flashes
 *                   on-screen later if we want; for now just a banner)
 *   active (~14s) — shards travel across the world along the chosen
 *                   axis, spread perpendicular to it. Collision uses
 *                   the existing obstacle-style bounce + damage path.
 *   cooldown      — no shards in the world; next storm scheduled.
 *
 * Shards are smaller, faster, and visually scrappier than the static
 * asteroid field. They despawn when they exit the world's storm
 * margin so the world doesn't fill with debris over time.
 */

import { Container, Graphics } from 'pixi.js';

interface Shard {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Outer radius for collision. Same convention as Obstacle. */
  radius: number;
  /** Per-shard angular velocity for the tumble. */
  rotRate: number;
  rot: number;
  /** Per-shard polygon — drawn once at construction; rotated in update. */
  g: Graphics;
}

export interface StormStrike {
  x: number;
  y: number;
}

export interface StormBanner {
  title: string;
  subtitle: string;
}

const ENABLE_BIOMASS = 800_000;
const STORM_WARN_SEC = 3.0;
const STORM_ACTIVE_SEC = 14.0;
const STORM_COOLDOWN_MIN = 65;
const STORM_COOLDOWN_MAX = 95;
// Storm shards are smaller and far faster than the static asteroids —
// they're meant to read as "incoming debris", not landscape.
const SHARD_RADIUS_MIN = 28;
const SHARD_RADIUS_MAX = 52;
const SHARD_SPEED_MIN = 240;
const SHARD_SPEED_MAX = 380;
// How many shard slots along the spawn line. Some are intentionally
// skipped (DROP_PROB) so the front always has at least one safe
// corridor — without that, a perfectly-spaced wall would feel unfair.
const SHARD_COUNT = 14;
const SHARD_DROP_PROB = 0.18;
// Margin beyond the world before a flying shard despawns. Generous so
// shards spawn fully off-screen and exit fully off-screen, never
// popping in or out at the visible edge.
const STORM_MARGIN = 600;

type Phase = 'idle' | 'warn' | 'active';

export class StormField {
  private parent: Container;
  private worldW: number;
  private worldH: number;
  private shards: Shard[] = [];
  private phase: Phase = 'idle';
  private phaseT = 0;
  private cooldownT: number;
  private pendingBanners: StormBanner[] = [];
  private rand: () => number;

  constructor(parent: Container, worldW: number, worldH: number) {
    this.parent = parent;
    this.worldW = worldW;
    this.worldH = worldH;
    let seed = 0x57E1A1;
    this.rand = () => {
      seed = (seed * 1664525 + 1013904223) | 0;
      return ((seed >>> 0) % 100000) / 100000;
    };
    // First storm is delayed even after the biomass threshold trips so
    // the unlock banner has room to breathe.
    this.cooldownT = 30 + this.rand() * 20;
  }

  update(dt: number, biomass: number): void {
    // Drive the active shards regardless of phase — once a storm has
    // launched, its shards keep flying even if the phase advanced.
    for (let i = this.shards.length - 1; i >= 0; i--) {
      const s = this.shards[i];
      s.x += s.vx * dt;
      s.y += s.vy * dt;
      s.rot += s.rotRate * dt;
      s.g.position.set(s.x, s.y);
      s.g.rotation = s.rot;
      // Despawn shards that have flown clear of the world.
      if (
        s.x < -STORM_MARGIN || s.x > this.worldW + STORM_MARGIN ||
        s.y < -STORM_MARGIN || s.y > this.worldH + STORM_MARGIN
      ) {
        s.g.destroy();
        this.shards.splice(i, 1);
      }
    }

    if (biomass < ENABLE_BIOMASS) return;

    this.phaseT += dt;

    if (this.phase === 'idle') {
      this.cooldownT -= dt;
      if (this.cooldownT <= 0) {
        this.phase = 'warn';
        this.phaseT = 0;
        this.pendingBanners.push({
          title: 'A debris storm is closing in',
          subtitle: 'Find a corridor — wait for it to pass',
        });
      }
      return;
    }

    if (this.phase === 'warn') {
      if (this.phaseT >= STORM_WARN_SEC) {
        this.phase = 'active';
        this.phaseT = 0;
        this.spawnFront();
      }
      return;
    }

    // active
    if (this.phaseT >= STORM_ACTIVE_SEC) {
      this.phase = 'idle';
      this.phaseT = 0;
      this.cooldownT = STORM_COOLDOWN_MIN + this.rand() * (STORM_COOLDOWN_MAX - STORM_COOLDOWN_MIN);
    }
  }

  private spawnFront(): void {
    // Pick a roughly axis-aligned direction with a bit of slant so the
    // storm doesn't always feel like the same horizontal/vertical line.
    // We bias toward horizontal because most viewports are wider than
    // tall, so a horizontal sweep covers more screen.
    const horizontal = this.rand() < 0.7;
    let angle: number;
    if (horizontal) {
      angle = (this.rand() < 0.5 ? 0 : Math.PI) + (this.rand() - 0.5) * 0.5;
    } else {
      angle = (this.rand() < 0.5 ? Math.PI / 2 : -Math.PI / 2) + (this.rand() - 0.5) * 0.5;
    }
    const dx = Math.cos(angle);
    const dy = Math.sin(angle);
    // Perpendicular axis (where shards spread out).
    const px = -dy;
    const py = dx;

    // Anchor the spawn line on the back side of the world relative to
    // the motion direction. Half-diagonal of the world ensures shards
    // start fully off-screen even on the wider axis.
    const half = Math.max(this.worldW, this.worldH) * 0.55;
    const cx = this.worldW / 2 - dx * half;
    const cy = this.worldH / 2 - dy * half;

    const lineLen = Math.max(this.worldW, this.worldH) * 1.05;

    for (let i = 0; i < SHARD_COUNT; i++) {
      // Skip occasional slots so the wall always has corridors. Keeps
      // the storm from feeling like a flat insta-kill barrier.
      if (this.rand() < SHARD_DROP_PROB) continue;
      const t = (i / (SHARD_COUNT - 1)) - 0.5;
      // Light per-slot jitter so corridor widths vary frame-to-frame.
      const jitter = (this.rand() - 0.5) * 220;
      const off = t * lineLen + jitter;
      const sx = cx + px * off;
      const sy = cy + py * off;
      const speed = SHARD_SPEED_MIN + this.rand() * (SHARD_SPEED_MAX - SHARD_SPEED_MIN);
      const radius = SHARD_RADIUS_MIN + this.rand() * (SHARD_RADIUS_MAX - SHARD_RADIUS_MIN);
      this.spawnShard(sx, sy, dx * speed, dy * speed, radius);
    }
  }

  private spawnShard(x: number, y: number, vx: number, vy: number, radius: number): void {
    const g = new Graphics();
    this.parent.addChild(g);
    // Jagged rock silhouette — six to nine vertices with random radial
    // jitter. Small enough that we draw once and just rotate.
    const verts = 6 + Math.floor(this.rand() * 4);
    let started = false;
    for (let i = 0; i <= verts; i++) {
      const a = (i / verts) * Math.PI * 2;
      const r = radius * (0.78 + this.rand() * 0.32);
      const vx0 = Math.cos(a) * r;
      const vy0 = Math.sin(a) * r;
      if (!started) { g.moveTo(vx0, vy0); started = true; }
      else g.lineTo(vx0, vy0);
    }
    g.closePath();
    // Two-tone rock — base shadow plus a lit highlight offset toward
    // the upper-left so even when tumbling the shard reads volumetric.
    g.fill({ color: 0x2a2230, alpha: 0.92 });
    g.stroke({ color: 0x5a4a66, width: 1.4, alpha: 0.85 });
    // A pale highlight cap, drawn on the tumbling body — rotates with
    // the shard, which is fine: tumbling rocks have wandering specular.
    g.circle(-radius * 0.32, -radius * 0.28, radius * 0.42).fill({ color: 0x8a749a, alpha: 0.45 });
    g.circle(-radius * 0.45, -radius * 0.40, radius * 0.18).fill({ color: 0xd0c0e0, alpha: 0.7 });

    const rotRate = (this.rand() < 0.5 ? -1 : 1) * (0.6 + this.rand() * 1.6);

    this.shards.push({
      x, y, vx, vy, radius, rotRate, rot: this.rand() * Math.PI * 2, g,
    });
  }

  /**
   * First shard whose collision circle overlaps the cluster's reach.
   * Returns the shard's position so the stage can play the same
   * obstacle-bounce response as a normal asteroid hit.
   */
  collideWith(cx: number, cy: number, r: number): StormStrike | null {
    for (const s of this.shards) {
      const reach = r + s.radius * 0.85;
      const dx = s.x - cx;
      const dy = s.y - cy;
      if (dx * dx + dy * dy <= reach * reach) {
        return { x: s.x, y: s.y };
      }
    }
    return null;
  }

  drainBanners(): StormBanner[] {
    if (this.pendingBanners.length === 0) return [];
    const out = this.pendingBanners;
    this.pendingBanners = [];
    return out;
  }

  destroy(): void {
    for (const s of this.shards) s.g.destroy();
    this.shards = [];
  }
}
